import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCurrentOrganizationStore } from '@/stores/currentOrganization'
import { watch } from 'vue'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/config'
import Dashboard from '../views/Dashboard.vue'
import Login from '../views/auth/Login.vue'
import Register from '../views/auth/Register.vue'
import CashFlow from '../views/CashFlow.vue'
import CostCenter from '../views/settings/CostCenter.vue'
import AccountsManagement from '../views/AccountsManagement.vue'
import BankManagement from '../views/settings/BankManagement.vue'
import SupplierManagement from '../views/settings/SupplierManagement.vue'
import CustomerManagement from '../views/settings/CustomerManagement.vue'
import PaymentStatusManagement from '../views/settings/PaymentStatusManagement.vue'
import MyAccount from '../views/MyAccount.vue'
import ProfileManagement from '../views/settings/ProfileManagement.vue'
import OrganizationsView from '../views/OrganizationsView.vue'
import UserManagement from '../views/UserManagement.vue'
import LoadingView from '../views/LoadingView.vue'
import { organizationGuard } from './middleware/organizationGuard'

// Função para restaurar a última rota
const restoreLastRoute = async (to: any, from: any, next: any) => {
  const currentOrganizationStore = useCurrentOrganizationStore()
  const lastRoute = localStorage.getItem('lastRoute')
  const orgId = localStorage.getItem('currentOrganizationId')
  
  // Primeiro garantir que temos uma organização
  if (orgId) {
    await currentOrganizationStore.loadSavedOrganization()
  }

  // Se estamos indo para o dashboard e temos uma última rota diferente
  if (lastRoute && to.path === '/dashboard' && lastRoute !== '/dashboard') {
    next(lastRoute)
    return
  }

  next()
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: { requiresGuest: true }
    },
    {
      path: '/register',
      name: 'register',
      component: Register,
      meta: { requiresGuest: true }
    },
    {
      path: '/organizations',
      name: 'organizations',
      component: OrganizationsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/my-account',
      name: 'my-account',
      component: MyAccount,
      meta: { requiresAuth: true }
    },
    {
      path: '/profile-management',
      name: 'profile-management',
      component: ProfileManagement,
      meta: { 
        requiresAuth: true,
        title: 'Perfis'
      }
    },
    {
      path: '/user-management',
      name: 'user-management',
      component: UserManagement,
      meta: { 
        requiresAuth: true,
        title: 'Gerenciar Usuários'
      }
    },
    {
      path: '/loading',
      name: 'loading',
      component: LoadingView
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/user/Profile.vue'),
      meta: { requiresAuth: true }
    },
    // Rotas que requerem uma organização selecionada
    {
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: { requiresAuth: true, requiresOrganization: true },
      beforeEnter: [restoreLastRoute, organizationGuard]
    },
    {
      path: '/accounts',
      name: 'accounts',
      component: AccountsManagement,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Contas a Pagar e Receber'
      },
      beforeEnter: organizationGuard
    },
    {
      path: '/cash-flow',
      name: 'cash-flow',
      component: CashFlow,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Fluxo de Caixa'
      },
      beforeEnter: organizationGuard
    },
    {
      path: '/cost-centers',
      name: 'cost-centers',
      component: CostCenter,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Centro de Custos'
      },
      beforeEnter: organizationGuard
    },
    {
      path: '/banks',
      name: 'banks',
      component: BankManagement,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Bancos'
      },
      beforeEnter: organizationGuard
    },
    {
      path: '/suppliers',
      name: 'suppliers',
      component: SupplierManagement,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Fornecedores'
      },
      beforeEnter: organizationGuard
    },
    {
      path: '/customers',
      name: 'customers',
      component: CustomerManagement,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Clientes'
      },
      beforeEnter: organizationGuard
    },
    {
      path: '/payment-status',
      name: 'payment-status',
      component: PaymentStatusManagement,
      meta: { 
        requiresAuth: true,
        requiresOrganization: true,
        title: 'Status de Pagamento'
      },
      beforeEnter: organizationGuard
    },
    // Catch all undefined routes and redirect to dashboard
    {
      path: '/:pathMatch(.*)*',
      redirect: '/dashboard'
    }
  ]
})

// Configuração do router guard global
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const currentOrganizationStore = useCurrentOrganizationStore()

  // Salvar a rota atual se não for uma rota de autenticação
  if (!to.meta.requiresGuest && to.path !== '/loading') {
    localStorage.setItem('lastRoute', to.path)
  }

  if (to.meta.requiresGuest && authStore.isAuthenticated) {
    next('/dashboard')
    return
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next('/login')
    return
  }

  if (to.meta.requiresOrganization && !currentOrganizationStore.currentOrganization) {
    // Se não tem organização, tenta carregar a salva
    const orgId = localStorage.getItem('currentOrganizationId')
    if (orgId) {
      try {
        await currentOrganizationStore.loadSavedOrganization()
        next()
        return
      } catch (error) {
        console.error('Error loading saved organization:', error)
        next('/organizations')
        return
      }
    } else {
      next('/organizations')
      return
    }
  }

  next()
})

// Salva a rota atual após cada navegação bem-sucedida
router.afterEach((to) => {
  if (to.meta.requiresOrganization) {
    localStorage.setItem('lastRoute', to.fullPath)
  }
})

export default router